import { useMemo, useState } from "react";
import {
    alpha,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    MenuItem,
    TextField,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { makeStyles } from "@/compat/muiStyles";
import { format } from "date-fns";
import { useSnackbar } from "notistack";
import ResponsiveSelect from "../../../components/ui/ResponsiveSelect";
import { KeyboardDatePicker } from "@/compat/pickers";
import { ratingList } from "../../constants/actionRatingConstants";
import useBulkUpdateActions from "../../hooks/mutations/useBulkUpdateActions";
import type { SkippedActionDTO } from "../../services/actionService";

const useStyles = makeStyles((theme) => ({
    bar: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        padding: theme.spacing(1, 1.5),
        marginBottom: theme.spacing(1),
        borderRadius: theme.shape.borderRadius,
        backgroundColor: alpha(theme.palette.secondary.main, 0.08),
    },
    count: {
        fontWeight: 600,
        marginRight: theme.spacing(1),
    },
    clear: {
        marginLeft: "auto",
    },
    field: {
        marginTop: theme.spacing(1),
        width: "100%",
    },
    confirm: {
        marginTop: theme.spacing(2),
        marginBottom: 0,
    },
}));

type BulkChange = "responsible" | "dueDate" | "priority" | "complete";

const changeTitles: Record<BulkChange, string> = {
    responsible: "Change responsible person",
    dueDate: "Change due date",
    priority: "Change priority",
    complete: "Mark as complete",
};

interface BulkActionUpdateProps {
    selected: any[];
    contacts: Record<string, any>;
    onClear: () => void;
    onCompleted: (rows: any[]) => void;
}

export default function BulkActionUpdate({ selected, contacts, onClear, onCompleted }: BulkActionUpdateProps) {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const bulkUpdate = useBulkUpdateActions();

    const [change, setChange] = useState<BulkChange | null>(null);
    const [assignee, setAssignee] = useState("");
    const [dueDate, setDueDate] = useState<Date | null>(null);
    const [rating, setRating] = useState<number | "">("");
    const [skipped, setSkipped] = useState<{ description: string; reason: string }[]>([]);

    const count = selected.length;
    const actionsLabel = `${count} action${count === 1 ? "" : "s"}`;

    const assigneeOptions = useMemo(() => {
        const siteIds = [...new Set(selected.map((x) => x.siteExternalId))];
        return Object.values(contacts ?? {})
            .filter((contact: any) => siteIds.every((siteId) => contact.accountIds?.includes(siteId)))
            .sort((a: any, b: any) => (a.firstName || "").localeCompare(b.firstName || ""));
    }, [selected, contacts]);

    const closeDialog = () => {
        setChange(null);
        setAssignee("");
        setDueDate(null);
        setRating("");
    };

    const confirmText = () => {
        switch (change) {
            case "responsible": {
                const contact = contacts?.[assignee];
                return contact ? `${actionsLabel} will be assigned to ${contact.firstName} ${contact.lastName}.` : "";
            }
            case "dueDate":
                return dueDate ? `${actionsLabel} will be due on ${format(dueDate, "dd MMM yyyy")}.` : "";
            case "priority":
                return rating !== "" ? `${actionsLabel} will be set to ${ratingList[rating].label} priority. Audit actions keep the priority set by your consultant.` : "";
            case "complete":
                return `${actionsLabel} will be marked as complete.`;
            default:
                return "";
        }
    };

    const canApply =
        (change === "responsible" && !!assignee) ||
        (change === "dueDate" && !!dueDate) ||
        (change === "priority" && rating !== "") ||
        change === "complete";

    const apply = () => {
        const contactNames = Object.fromEntries(
            [assignee, ...selected.map((x) => x.assignedEmployeeExternalId)]
                .filter((id) => contacts?.[id])
                .map((id) => [id, `${contacts[id].firstName} ${contacts[id].lastName}`])
        );

        const dto = {
            actions: selected.map((x) => ({ id: x.id, isDri: !!x.isDri })),
            contactNames: change === "responsible" ? contactNames : null,
            assignedEmployeeExternalId: change === "responsible" ? assignee : null,
            dueDate: change === "dueDate" && dueDate ? format(dueDate, "yyyy-MM-dd'T'23:59:59") : null,
            rating: change === "priority" ? rating : null,
            isComplete: change === "complete" ? true : null,
        };

        bulkUpdate.mutate(dto, {
            onSuccess: (skippedActions: SkippedActionDTO[]) => {
                const skippedRow = (row) => skippedActions.find((x) => x.id === row.id && x.isDri === !!row.isDri);
                const updated = selected.filter((row) => !skippedRow(row));
                if (change === "complete") onCompleted(updated);
                setSkipped(selected.filter(skippedRow).map((row) => ({ description: row.description, reason: skippedRow(row).reason })));
                closeDialog();
                onClear();
                if (!skippedActions.length)
                    enqueueSnackbar(`Updated ${updated.length} action${updated.length === 1 ? "" : "s"}`, { variant: "success" });
            },
            onError: (error) => {
                console.error(error);
                enqueueSnackbar("Could not update the selected actions.", { variant: "error" });
            },
        });
    };

    return (
        <>
            {count > 0 && (
                <div className={classes.bar}>
                    <Typography variant="body2" className={classes.count}>
                        {count} selected
                    </Typography>
                    <Button size="small" variant="outlined" color="secondary" onClick={() => setChange("responsible")}>
                        Reassign
                    </Button>
                    <Button size="small" variant="outlined" color="secondary" onClick={() => setChange("dueDate")}>
                        Due date
                    </Button>
                    <Button size="small" variant="outlined" color="secondary" onClick={() => setChange("priority")}>
                        Priority
                    </Button>
                    <Button size="small" variant="outlined" color="secondary" onClick={() => setChange("complete")}>
                        Complete
                    </Button>
                    <IconButton size="small" className={classes.clear} onClick={onClear} aria-label="Clear selection">
                        <Close fontSize="small" />
                    </IconButton>
                </div>
            )}
            <Dialog open={!!change} onClose={closeDialog} fullWidth={change !== "complete"} maxWidth="xs">
                <DialogTitle>{change && changeTitles[change]}</DialogTitle>
                <DialogContent>
                    {change === "responsible" && (
                        <ResponsiveSelect
                            className={classes.field}
                            label="Responsible"
                            options={assigneeOptions}
                            optionLabelFunction={(x) => `${x.firstName} ${x.lastName}`}
                            optionValueKey="externalId"
                            value={assignee}
                            onChange={(e) => setAssignee(e.target.value)}
                            helperText={assigneeOptions.length ? "Only people who can see every selected site are listed." : "Nobody can see every selected site."}
                        />
                    )}
                    {change === "dueDate" && (
                        <KeyboardDatePicker
                            className={classes.field}
                            format="dd/MM/yyyy"
                            inputVariant="outlined"
                            label="Due date"
                            value={dueDate}
                            onChange={setDueDate}
                        />
                    )}
                    {change === "priority" && (
                        <TextField
                            select
                            size="small"
                            className={classes.field}
                            label="Priority"
                            value={rating}
                            onChange={(e) => setRating(Number(e.target.value))}
                        >
                            {ratingList.map((x) => (
                                <MenuItem key={x.value} value={x.value}>
                                    {x.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                    <DialogContentText className={change === "complete" ? undefined : classes.confirm}>
                        {confirmText()}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} color="secondary">
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        disabled={!canApply || bulkUpdate.isLoading}
                        onClick={apply}
                    >
                        {change === "complete" ? "Complete" : "Update"}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={!!skipped.length} onClose={() => setSkipped([])} fullWidth maxWidth="sm">
                <DialogTitle>{skipped.length} action{skipped.length === 1 ? " wasn't" : "s weren't"} changed</DialogTitle>
                <DialogContent>
                    <List dense>
                        {skipped.map((x, index) => (
                            <ListItem key={index} disableGutters>
                                <ListItemText primary={x.description} secondary={x.reason} />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color="secondary" onClick={() => setSkipped([])}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
